/**
 * friendlyErrorMessage — turn any API error into words a shop owner or
 * cashier understands. Applied once in the API client (services/api.ts), so
 * every toast that shows error.message gets the friendly version.
 *
 * Rules:
 *  - No connection / timeout → plain network advice.
 *  - 401 → session ended.  403 → "you don't have permission to …" with the
 *    action in plain words.  429 → slow down.  5xx → our fault, try again.
 *  - Otherwise the server's own message, unless it looks technical
 *    (database / code errors), in which case a plain message by status.
 */
import axios from "axios";

const PERMISSION_WORDS: Record<string, string> = {
  "order.cancel": "cancel sales",
  "order.create": "make sales",
  "order.update": "change sales",
  "order.edit-completed": "edit completed sales",
  "order.read": "see sales",
  "product.create": "add products",
  "product.update": "change products",
  "product.archive": "remove products",
  "product.stock.add": "receive stock",
  "product.stock.adjust": "adjust stock",
  "product.stock.set": "set stock levels",
  "product.tier-price.update": "change prices",
  "product.cost.update": "change product costs",
  "product.update.bulk": "edit products in bulk",
  "purchase-order.approve": "approve purchase orders",
  "purchase-order.create": "create purchase orders",
  "purchase-order.read": "see purchase orders",
  "expenditure.approve": "approve expenses",
  "credit.override": "approve sales above a customer's credit limit",
  "customer.tier.change": "change a customer's price level",
  "report.inventory.read": "see stock reports",
  "handshake.create": "place orders with partners",
  "connection.close": "close partner connections",
  "subscription.change-plan": "change the subscription plan",
  "opening-balance.set": "set opening balances",
  "business.update": "change business settings",
  "company.update": "change company settings",
};

const TECHNICAL = [
  /cast to objectid/i,
  /e11000/i,
  /buffering timed out/i,
  /econn|etimedout|socket hang up/i,
  /cannot read propert/i,
  /is not defined/i,
  /undefined|null pointer/i,
  /validation failed: \w+: path/i,
  /mongo|mongoose/i,
  /request failed with status code/i,
  /^route not found/i,
  /internal server error/i,
];

const looksTechnical = (msg: string) => TECHNICAL.some((re) => re.test(msg));

function serverMessage(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const d = data as { message?: unknown; error?: unknown; errors?: Array<{ msg?: string; message?: string }> };
  if (typeof d.message === "string" && d.message.trim() && d.message !== "Validation failed") return d.message.trim();
  if (Array.isArray(d.errors) && d.errors.length) {
    const first = d.errors[0];
    const m = first?.msg ?? first?.message;
    if (typeof m === "string" && m.trim()) return m.trim();
  }
  if (typeof d.error === "string" && d.error.trim() && d.error !== "Validation failed") return d.error.trim();
  return null;
}

export function friendlyErrorMessage(err: unknown, fallback = "Something went wrong. Please try again."): string {
  if (!axios.isAxiosError(err)) {
    return err instanceof Error && err.message && !looksTechnical(err.message) ? err.message : fallback;
  }
  if (err.code === "ECONNABORTED") return "This is taking longer than usual. Please try again.";
  if (!err.response) {
    return typeof navigator !== "undefined" && navigator.onLine === false
      ? "You're offline. Check your internet connection and try again."
      : "Can't reach Eaze right now. Check your internet connection and try again.";
  }

  const status = err.response.status;
  const msg = serverMessage(err.response.data);

  if (status === 401) return "Your session has ended. Please sign in again.";
  if (status === 403) {
    const perm = msg?.match(/missing permission:\s*([a-z0-9.-]+)/i)?.[1];
    if (perm) {
      const words = PERMISSION_WORDS[perm];
      return words
        ? `You don't have permission to ${words}. Ask your manager or the business owner.`
        : "You don't have permission to do this. Ask your manager or the business owner.";
    }
    if (msg && !looksTechnical(msg) && !/^access denied|^forbidden/i.test(msg)) return msg;
    return "You don't have permission to do this. Ask your manager or the business owner.";
  }
  if (status === 429) return msg && !looksTechnical(msg) ? msg : "Too many attempts. Please wait a moment and try again.";
  if (status >= 500) return "Something went wrong on our side. Please try again — if it keeps happening, contact support.";
  if (status === 404) {
    return msg && !looksTechnical(msg) && !/^not found$/i.test(msg)
      ? msg
      : "We couldn't find that. It may have been removed — refresh the page.";
  }
  if (msg && !looksTechnical(msg)) return msg;
  if (status === 409) return "This conflicts with something that already exists. Refresh and try again.";
  if (status === 400 || status === 422) return "Some details aren't right. Please check and try again.";
  return fallback;
}

export default friendlyErrorMessage;
